import React, { useState, useEffect, useRef, useCallback } from 'react';
import { InputComponent, DropDownComponent } from '../custcomponents';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import { AxiosAdminInstance } from '../utils';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

interface Blog {
    id: string;
    blogname: string;
    author: string;
    date: string;
    status: string;
    blogcontent: string;
    eventcode?: string;
    createdate: Date;
}

export const ManageBlogs = () => {
    const [blogs, setBlogs] = useState<Blog[]>([]);
    const [loading, setLoading] = useState(false);
    const [editingBlog, setEditingBlog] = useState<Blog | null>(null);
    const [newBlog, setNewBlog] = useState({
        blogname: '',
        author: '',
        date: '',
        status: 'ACTIVE',
        blogcontent: '',
        eventcode: ''
    });
    const [fullscreen, setFullscreen] = useState(false);

    // Custom toolbar handler for fullscreen
    const handleFullscreen = useCallback(() => {
        setFullscreen(prev => !prev);
    }, []);

    useEffect(() => {
        fetchBlogs();
    }, []);

    useEffect(() => {
        if (editingBlog) {
            setNewBlog({
                blogname: editingBlog.blogname,
                author: editingBlog.author,
                date: editingBlog.date,
                status: editingBlog.status,
                blogcontent: editingBlog.blogcontent,
                eventcode: editingBlog.eventcode || ''
            });
        } else {
            setNewBlog({
                blogname: '',
                author: '',
                date: '',
                status: 'ACTIVE',
                blogcontent: '',
                eventcode: ''
            });
        }
    }, [editingBlog]);

    const fetchBlogs = async () => {
        setLoading(true);
        try {
            const response = await AxiosAdminInstance.get('/api/admin/manageblogs?admin=true');
            if (response.data.result === 'Success') {
                setBlogs(response.data.data || []);
            } else {
                toast.error(response.data.msg || 'Failed to fetch blogs');
            }
        } catch (error) {
            toast.error('Failed to fetch blogs');
        }
        setLoading(false);
    };

    const handleAddBlog = async () => {
        if (!newBlog.blogname.trim()) {
            toast.error('Blog name is required');
            return;
        }
        if (!newBlog.author.trim()) {
            toast.error('Author name is required');
            return;
        }
        if (!newBlog.date.trim()) {
            toast.error('Date is required');
            return;
        }
        if (!newBlog.blogcontent.trim()) {
            toast.error('Blog content is required');
            return;
        }
        
        setLoading(true);
        try {
            const data = {
                blogname: newBlog.blogname,
                author: newBlog.author,
                date: newBlog.date,
                status: newBlog.status,
                blogcontent: newBlog.blogcontent,
                eventcode: newBlog.eventcode || null
            };

            let response;
            if (editingBlog) {
                response = await AxiosAdminInstance.put('/api/admin/manageblogs', data, {
                    params: { blogId: editingBlog.id }
                });
            } else {
                response = await AxiosAdminInstance.post('/api/admin/manageblogs', data);
            }

            if (response.data.result === 'Success') {
                toast.success(editingBlog ? 'Blog updated successfully' : 'Blog added successfully');
                setNewBlog({
                    blogname: '',
                    author: '',
                    date: '',
                    status: 'ACTIVE',
                    blogcontent: '',
                    eventcode: ''
                });
                setEditingBlog(null);
                fetchBlogs();
            } else {
                toast.error(response.data.msg || 'Operation failed');
            }
        } catch (error) {
            toast.error(editingBlog ? 'Failed to update blog' : 'Failed to add blog');
        }
        setLoading(false);
    };

    const handleCancel = () => {
        setNewBlog({
            blogname: '',
            author: '',
            date: '',
            status: 'ACTIVE',
            blogcontent: '',
            eventcode: ''
        });
        setEditingBlog(null);
    };

    const handleDeleteBlog = async (blogId: string) => {
        if (!window.confirm('Are you sure you want to delete this blog?')) return;
        setLoading(true);
        try {
            const response = await AxiosAdminInstance.delete('/api/admin/manageblogs', {
                params: { blogId }
            });
            if (response.data.result === 'Success') {
                toast.success('Blog deleted successfully');
                fetchBlogs();
            } else {
                toast.error(response.data.msg || 'Failed to delete blog');
            }
        } catch (error) {
            toast.error('Failed to delete blog');
        } finally {
            setLoading(false);
        }
    };

    const handleFullscreenToggle = () => {
        setFullscreen(!fullscreen);
    };

    const blogColumns = [
        { 
            name: 'Actions', 
            cell: (row: Blog) => (
                <div>
                    <button className="btn btn-sm btn-warning me-1" onClick={() => setEditingBlog(row)} title="Edit">
                        <i className="fa fa-edit"></i>
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDeleteBlog(row.id)} title="Delete">
                        <i className="fa fa-trash"></i>
                    </button>
                </div>
            ) 
        },
        { 
            name: 'Blog Name', 
            selector: (row: Blog) => row.blogname, 
            sortable: true,
            width: '200px'
        },
        { 
            name: 'Author', 
            selector: (row: Blog) => row.author, 
            sortable: true,
            width: '150px'
        },
        { 
            name: 'Date', 
            selector: (row: Blog) => row.date, 
            sortable: true,
            width: '150px'
        },
        { 
            name: 'Event Code', 
            selector: (row: Blog) => row.eventcode || 'N/A', 
            sortable: true,
            width: '150px'
        },
        { 
            name: 'Status', 
            selector: (row: Blog) => row.status, 
            sortable: true,
            width: '120px'
        },
        { 
            name: 'Created Date', 
            selector: (row: Blog) => new Date(row.createdate).toLocaleDateString(), 
            sortable: true,
            width: '150px'
        },
        { 
            name: 'Content Preview', 
            cell: (row: Blog) => (
                <div style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.blogcontent}>
                    {row.blogcontent.length > 100 ? `${row.blogcontent.substring(0, 100)}...` : row.blogcontent}
                </div>
            ), 
            sortable: false 
        }
    ];

    return (
        <>
            <section className="register-sec sec-padding-admin" data-scroll-index="8">
                <div className="container">
                    <div className="sec-title">
                        <span className="icon-inner wow flipInY" data-wow-duration="300ms">
                            <span className="fa-stack">
                                <i className="fa rhex fa-stack-2x"></i>
                                <i className="fa fa-newspaper-o fa-stack-1x"></i>
                            </span>
                        </span>
                        <h3 className="wow fadeInRight" data-wow-duration="500ms">Manage Blogs</h3>
                    </div>
                    <div className="register-sec-details">
                        <form onSubmit={(e) => { e.preventDefault(); handleAddBlog(); }} noValidate>
                            <fieldset className="form-group p-3">
                                <legend className="w-auto px-2 subheading">{editingBlog ? 'Edit Blog' : 'Add New Blog'}</legend>
                                <div className="row pt-2">
                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                        <InputComponent
                                            custname="blogname"
                                            custplaceholder="Blog Name"
                                            custid="blogName"
                                            onExternalValue={(val: string) => setNewBlog(prev => ({ ...prev, blogname: val }))}
                                            onExternalBind={newBlog.blogname}
                                            type="text"
                                        />
                                    </div>
                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                        <InputComponent
                                            custname="author"
                                            custplaceholder="Author Name"
                                            custid="blogAuthor"
                                            onExternalValue={(val: string) => setNewBlog(prev => ({ ...prev, author: val }))}
                                            onExternalBind={newBlog.author}
                                            type="text"
                                        />
                                    </div>
                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                        <InputComponent
                                            custname="date"
                                            custplaceholder="Blog Date (YYYY-MM-DD)"
                                            custid="blogDate"
                                            onExternalValue={(val: string) => setNewBlog(prev => ({ ...prev, date: val }))}
                                            onExternalBind={newBlog.date}
                                            type="date"
                                        />
                                    </div>
                                </div>
                                <div className="row pt-2">
                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                        <InputComponent
                                            custname="eventcode"
                                            custplaceholder="Event Code (Optional)"
                                            custid="blogEventCode"
                                            onExternalValue={(val: string) => setNewBlog(prev => ({ ...prev, eventcode: val }))}
                                            onExternalBind={newBlog.eventcode}
                                            type="text"
                                        />
                                    </div>
                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                        <DropDownComponent
                                            custname="status"
                                            custplaceholder="Select Status"
                                            custid="blogStatus"
                                            onExternalValue={(val: string) => setNewBlog(prev => ({ ...prev, status: val }))}
                                            externalstateValue={newBlog.status}
                                            names={['ACTIVE', 'INACTIVE']}
                                        />
                                    </div>
                                </div>
                                <div className="row pt-2">
                                    <div className="col-lg-12 col-md-12 col-sm-12">
                                        <div className="form-group">
                                            <label htmlFor="blogContent">Blog Content</label>
                                            <div className={fullscreen ? 'editor-fullscreen' : 'editor-normal'}>
                                                <ReactQuill
                                                    id="blogContent"
                                                    value={newBlog.blogcontent}
                                                    onChange={(value) => setNewBlog(prev => ({ ...prev, blogcontent: value }))} 
                                                    style={{ height: '300px',  background: "white" }}
                                                    theme="snow"
                                                    modules={{
                                                        toolbar:{container: [
                                                            [{ 'header': [1, 2, 3, false] }],
                                                            ['bold', 'italic', 'underline', 'strike'],
                                                            [{'list': 'ordered'}, {'list': 'bullet'}],
                                                            ['link', 'image'],
                                                            ['clean'],
                                                            ['fullscreen']
                                                        ],
                                                        handlers: {
                                                            'fullscreen': handleFullscreen
                                                        }}
                                                    }}
                                                    formats={[
                                                        'header',
                                                        'bold', 'italic', 'underline', 'strike',
                                                        'list', 'bullet',
                                                        'link', 'image'
                                                    ]}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="row pt-2">
                                    <div className="col-lg-6 col-md-6 col-sm-6">
                                        <button className="btn-default" type="submit" disabled={loading}>
                                            {editingBlog ? 'Update Blog' : 'Add Blog'} {loading && <i className="fa fa-circle-o-notch fa-spin"></i>}
                                        </button>
                                    </div>
                                    <div className="col-lg-6 col-md-6 col-sm-6">
                                        {editingBlog && (
                                            <button className="btn-default" type="button" onClick={handleCancel}>
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </fieldset>

                            <fieldset className="form-group p-3">
                                <legend className="w-auto px-2 subheading">Blog Items</legend>
                                <div className="row pt-2">
                                    <div className="col-lg-12 col-md-12 col-sm-12">
                                        <DataTable
                                            columns={blogColumns}
                                            data={blogs}
                                            customStyles={{
                                                cells: {
                                                    style: {
                                                        wordWrap: 'break-word',
                                                        whiteSpace: 'normal',
                                                        padding: '8px'
                                                    }
                                                }
                                            }}
                                            pagination
                                            paginationPerPage={10}
                                            paginationRowsPerPageOptions={[5, 10, 25, 50]}
                                        />
                                    </div>
                                </div>
                            </fieldset>
                        </form>
                    </div>
                </div>
            </section>
        </>
    );
};